import { Injectable, signal } from '@angular/core';
import Swal, { SweetAlertIcon } from 'sweetalert2';

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  errorMessage$ = signal<string>('');
  showErrorModal$ = signal<boolean>(false);
  showCloseSessionModal$ = signal<boolean>(false);

  showError(message: string): void {
    this.errorMessage$.set(message);
    this.showErrorModal$.set(true);

    // Use Bootstrap modal API if available
    if (typeof window !== 'undefined') {
      setTimeout(() => {
        const modalElement = document.getElementById('errorModal');
        if (modalElement) {
          const bootstrap = (window as any).bootstrap;
          if (bootstrap && bootstrap.Modal) {
            const modal = new bootstrap.Modal(modalElement);
            modal.show();
          }
        }
      }, 100);
    }
  }

  hideError(): void {
    this.showErrorModal$.set(false);
    this.errorMessage$.set('');
  }

  showCloseSession(): void {
    this.showCloseSessionModal$.set(true);

    // Use Bootstrap modal API if available
    if (typeof window !== 'undefined') {
      setTimeout(() => {
        const modalElement = document.getElementById('closeSessionModal');
        if (modalElement) {
          const bootstrap = (window as any).bootstrap;
          if (bootstrap && bootstrap.Modal) {
            const modal = new bootstrap.Modal(modalElement);
            modal.show();
          }
        }
      }, 100);
    }
  }

  hideCloseSession(): void {
    this.showCloseSessionModal$.set(false);
  }

  /**
   * Muestra un toast usando SweetAlert2
   * @param message Mensaje a mostrar
   * @param icon Tipo de icono: 'success' | 'error' | 'warning' | 'info' | 'question'
   * @param position Posición del toast
   * @param timer Tiempo en milisegundos (por defecto 3000)
   */
  showToast(
    message: string,
    icon: SweetAlertIcon = 'success',
    position:
      | 'top'
      | 'top-end'
      | 'top-start'
      | 'bottom'
      | 'bottom-end'
      | 'bottom-start' = 'top-end',
    timer: number = 3000
  ): void {
    const Toast = Swal.mixin({
      toast: true,
      position: position,
      showConfirmButton: false,
      timer: timer,
      timerProgressBar: true,
      iconColor: 'white',
      customClass: {
        popup: 'colored-toast',
      },
      didOpen: (toast) => {
        toast.onmouseenter = Swal.stopTimer;
        toast.onmouseleave = Swal.resumeTimer;
      },
    });

    Toast.fire({
      icon: icon,
      title: message,
    });
  }

  /**
   * Cierra cualquier toast o modal de SweetAlert2 visible
   */
  dismissToast(): void {
    Swal.close();
  }

  /**
   * Muestra un modal de confirmación usando SweetAlert2
   * @param message Mensaje de confirmación
   * @param title Título del modal
   * @param confirmButtonText Texto del botón de confirmación
   * @param cancelButtonText Texto del botón de cancelar
   * @returns Promise<boolean> true si el usuario confirma, false si cancela
   */
  async showConfirm(
    message: string,
    title: string = 'Confirmar',
    confirmButtonText: string = 'Confirmar',
    cancelButtonText: string = 'Cancelar'
  ): Promise<boolean> {
    const result = await Swal.fire({
      title: title,
      text: message,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#114b88',
      cancelButtonColor: '#ee2646',
      confirmButtonText: confirmButtonText,
      cancelButtonText: cancelButtonText,
      reverseButtons: true,
    });

    return result.isConfirmed;
  }

  /**
   * Muestra un alert simple usando SweetAlert2
   * @param message Mensaje a mostrar
   * @param title Título del alert
   * @param icon Tipo de icono
   */
  showAlert(message: string, title: string = '', icon: SweetAlertIcon = 'info'): void {
    Swal.fire({
      title: title,
      text: message,
      icon: icon,
      confirmButtonText: 'Aceptar',
    });
  }
}
